<script lang="ts">
  import type { Person } from '../lib/types'
  import { Dialog, Input, Textarea, Label, Button, Checkbox } from '$lib/components/ui'

  interface Props {
    person: Person
    videoId: string
    onSave: (person: Person) => void
    onCancel: () => void
  }

  let { person, videoId, onSave, onCancel }: Props = $props()

  let name = $state(person.name)
  let description = $state(person.description || '')
  let aliases = $state(person.aliases?.join(', ') || '')
  let internalOnly = $state(person.internalOnly || false)
  let isSaving = $state(false)
  let open = $state(true)

  async function handleSave() {
    if (!name.trim()) return

    isSaving = true
    try {
      const aliasesList = aliases
        .split(',')
        .map(a => a.trim())
        .filter(a => a.length > 0)

      const payload = {
        name: name.trim(),
        description: description.trim(),
        aliases: aliasesList,
        internal_only: internalOnly,
        video_id: videoId,
      }

      const response = await fetch(`/api/persons/${person.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        const savedPerson = await response.json()
        onSave(savedPerson)
      }
    } finally {
      isSaving = false
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) onCancel()
  }
</script>

<Dialog bind:open onOpenChange={handleOpenChange} title="Edit person">
  <div class="space-y-4">
    <div>
      <Label for="name">Name</Label>
      <Input id="name" bind:value={name} placeholder="Person name" />
    </div>

    <div>
      <Label for="aliases">Aliases (comma-separated)</Label>
      <Input id="aliases" bind:value={aliases} placeholder="Alternative names" />
    </div>

    <div>
      <Label for="description">Description</Label>
      <Textarea id="description" bind:value={description} placeholder="Description" rows={3} />
    </div>

    <div class="flex items-center gap-2">
      <Checkbox bind:checked={internalOnly} />
      <span class="text-sm text-text-secondary">Internal only (don't match cross-video)</span>
    </div>

    <div class="flex justify-end gap-2 pt-4 border-t border-border-subtle">
      <Button variant="ghost" onclick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onclick={handleSave} disabled={isSaving || !name.trim()}>
        {isSaving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  </div>
</Dialog>
